import { ChangeDetectorRef, Component, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LeapV2UploadedFile } from '../models/leap-v2-student.model';
import { DynamicConfirmModalComponent } from '../../../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { EVIDENCE_SIZE_HINT, MAX_EVIDENCE_FILE_BYTES, MAX_EVIDENCE_NEW_BYTES_PER_SAVE } from '../../../../_constants/evidence-limits';

const MB = 1024 * 1024;

@Component({
  selector: 'leap-v2-evidence-upload',
  templateUrl: './leap-v2-evidence-upload.component.html',
  styleUrls: ['./leap-v2-evidence-upload.component.css'],
  standalone: false,
})
export class LeapV2EvidenceUploadComponent {
  @Input() disabled = false;
  @Output() filesChanged = new EventEmitter<LeapV2UploadedFile[]>();

  files: LeapV2UploadedFile[] = [];
  // Why files from the last pick were not attached, one line per file.
  sizeErrors: string[] = [];
  isDragOver = false;

  readonly accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.heic';

  constructor(private dialog: MatDialog, private cdr: ChangeDetectorRef) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(input.files);
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  /** Removes a picked file once the tutor confirms (40317: one click removed it with no prompt). */
  removeFile(name: string): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: `Remove ${name} from the evidence of work?`, confirmLabel: 'Remove' },
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.files = this.files.filter(f => f.name !== name);
        this.filesChanged.emit(this.files);
        this.cdr.markForCheck();
      });
  }

  fileIcon(name: string): string {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'heic'].includes(ext)) return '🖼️';
    if (ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    return '📎';
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Adds the picked files, refusing any over the per-file limit or that would take this save's new files over
   * the per-save total (40759: an oversized save failed with a bare 502). Every file here is new: the parent
   * screens list stored files separately and clear this control once a save stores them.
   */
  private addFiles(fileList: FileList): void {
    const existing = new Map(this.files.map(f => [f.name, f]));
    this.sizeErrors = [];
    Array.from(fileList).forEach(f => {
      const othersTotal = Array.from(existing.values())
        .filter(e => e.name !== f.name)
        .reduce((sum, e) => sum + (e.size ?? 0), 0);
      if (f.size > MAX_EVIDENCE_FILE_BYTES) {
        this.sizeErrors.push(`${f.name} (${this.formatSize(f.size)}) is too large to attach. Each file can be up to `
          + `${MAX_EVIDENCE_FILE_BYTES / MB} MB. ${EVIDENCE_SIZE_HINT}`);
      } else if (othersTotal + f.size > MAX_EVIDENCE_NEW_BYTES_PER_SAVE) {
        this.sizeErrors.push(`${f.name} (${this.formatSize(f.size)}) was not attached, as it would take the files `
          + `attached in this save over ${MAX_EVIDENCE_NEW_BYTES_PER_SAVE / MB} MB in total. ${EVIDENCE_SIZE_HINT}`);
      } else {
        existing.set(f.name, { name: f.name, size: f.size, file: f });
      }
    });
    this.files = Array.from(existing.values());
    this.filesChanged.emit(this.files);
    this.cdr.markForCheck();
  }
}
